import React, { useState, useEffect } from "react";
import { Row, Col, Card, Input, Button, Modal, Typography, Space, Tag, Tooltip, Dropdown, Menu, Avatar } from "antd";
import { useParams, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { BiArrowBack } from "react-icons/bi";
import { BsThreeDotsVertical } from "react-icons/bs";
import { ArrowLeft, Copy, Archive, Bot, Hash, FileText } from "lucide-react";
import {
  getGenerationById,
  archiveGeneration,
} from "@api/copilots/browser-extension/generation";
import { SuccessNotification, ErrorNotification } from "@components";
import logoSmall from "@assets/images/logo/logo.png";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

const GenerationDetails = () => {
  const { user } = useSelector((state) => state.auth);
  const { id } = useParams();
  const history = useHistory();
  const [generation, setGeneration] = useState(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) {
      return { date: 'Unknown date', time: 'Unknown time' };
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return { date: 'Invalid date', time: 'Invalid time' };
      }
      return {
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      };
    } catch (error) {
      console.error('Date formatting error:', error);
      return { date: 'Invalid date', time: 'Invalid time' };
    }
  };

  // Copy text function
  const copyText = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      SuccessNotification("Text copied to clipboard");
    });
  };

  useEffect(() => {
    handleFetchGenerationById();
  }, [id]);

  // Handle fetch generation by id
  const handleFetchGenerationById = async () => {
    setLoading(true);
    try {
      const { data } = await getGenerationById(id);

      // if data.is_continue
      if (data.is_continue) {
        let extraGen = "";
        let extraTokens = 0;

        // For each item if the continue_generation_details, get the generated_text and append it to the generated_text
        data.continue_generation_details.forEach((item) => {
          extraGen += item.generated_text;
        });
        data.generated_text += extraGen;

        // For each item if the continue_generation_details, get the tokens_count and add it to the tokens_count
        data.continue_generation_details.forEach((item) => {
          extraTokens += item.tokens_count;
        });
        data.tokens_count += extraTokens;
      }
      setGeneration(data);
    } catch (error) {
      ErrorNotification("Failed to load generation details");
      console.log("GET GENERATION BY ID ERROR", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle generation archive
  const handleGenerationArchive = (id) => {
    // Confirm delete
    Modal.confirm({
      title: "Are you sure you want to archive this generation?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          // Archive event
          await archiveGeneration(id);
          // Show success notification
          SuccessNotification("Generation archived successfully");

          // Redirect to history page
          history.push("/history");
        } catch (error) {
          // Show error notification
          ErrorNotification("Error archiving generation");
        }
      },
    });
  };

  const menu = (
    <Menu>
      <Menu.Item key="archive" onClick={() => handleGenerationArchive(generation?._id)}>
        Archive
      </Menu.Item>
    </Menu>
  );

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)"
      }}>
        <Card style={{ textAlign: 'center', borderRadius: '16px', padding: '40px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '32px', marginBottom: '24px', color: '#6c757d' }}>⚡</div>
          <Title level={4} style={{ color: '#495057', marginBottom: '8px' }}>Loading Generation Details</Title>
          <Text style={{ color: '#6c757d' }}>Retrieving generation data...</Text>
        </Card>
      </div>
    );
  }

  const { date, time } = generation ? formatDateTime(generation.createdAt) : { date: '...', time: '...' };

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
        minHeight: "100vh",
        padding: "24px"
      }}
    >
      {/* Modern Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          borderRadius: "20px",
          padding: "20px 24px",
          marginBottom: "24px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(255, 255, 255, 0.2)"
        }}
      >
        <Row align="middle" justify="space-between">
          <Col flex="auto">
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Button
                type="text"
                size="small"
                icon={<BiArrowBack size={16} />}
                onClick={() => history.goBack()}
                style={{
                  borderRadius: "8px",
                  background: "rgba(255, 255, 255, 0.8)",
                  border: "1px solid rgba(0, 0, 0, 0.1)",
                  height: "32px",
                  width: "32px",
                  padding: 0
                }}
              />
              <Avatar 
                src={user?.profilePicture || logoSmall}
                size={32}
                style={{ 
                  border: "2px solid #fff", 
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)" 
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "2px" }}>
                  <Title level={5} style={{ margin: 0, color: "#1a1a1a", fontSize: "16px" }}>
                    Generation Details
                  </Title>
                  <Tag color="blue" style={{ borderRadius: "8px", fontSize: "10px", padding: "0 6px", height: "18px", lineHeight: "18px" }}>
                    ID: {generation?._id?.slice(-6)}
                  </Tag>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Text style={{ fontSize: "11px", color: "#666" }}>
                    📅 {date} 🕒 {time}
                  </Text>
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "10px", color: "#495057", background: "rgba(248,249,250,0.8)", padding: "2px 6px", borderRadius: "6px", border: "1px solid rgba(0,0,0,0.05)" }}>
                      🎯 {generation?.category?.name || 'N/A'}
                    </span>
                    <span style={{ fontSize: "10px", color: "#495057", background: "rgba(248,249,250,0.8)", padding: "2px 6px", borderRadius: "6px", border: "1px solid rgba(0,0,0,0.05)" }}>
                      🤖 {generation?.copilot?.name || 'N/A'}
                    </span>
                    <span style={{ fontSize: "10px", color: "#495057", background: "rgba(248,249,250,0.8)", padding: "2px 6px", borderRadius: "6px", border: "1px solid rgba(0,0,0,0.05)" }}>
                      🔢 {generation?.tokens_count || 0} tokens
                    </span>
                    {generation?.is_continue && (
                      <span style={{ fontSize: "10px", color: "#28a745", background: "rgba(40,167,69,0.1)", padding: "2px 6px", borderRadius: "6px", border: "1px solid rgba(40,167,69,0.2)" }}>
                        📈 Extended
                      </span>
                    )}
                    {generation?.is_useful && (
                      <span style={{ fontSize: "10px", color: "#28a745", background: "rgba(40,167,69,0.1)", padding: "2px 6px", borderRadius: "6px", border: "1px solid rgba(40,167,69,0.2)" }}>
                        👍 Useful
                      </span>
                    )}
                    {generation?.is_poor && (
                      <span style={{ fontSize: "10px", color: "#dc3545", background: "rgba(220,53,69,0.1)", padding: "2px 6px", borderRadius: "6px", border: "1px solid rgba(220,53,69,0.2)" }}>
                        👎 Poor
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Col>
          
          <Col>
            <Space size="small">
              <Dropdown overlay={menu}>
                <Button
                  icon={<BsThreeDotsVertical size={16} />}
                  style={{ 
                    borderRadius: "10px",
                    background: "rgba(255, 255, 255, 0.8)",
                    border: "1px solid rgba(0, 0, 0, 0.1)"
                  }}
                />
              </Dropdown>
            </Space>
          </Col>
        </Row>
      </motion.div>

      {/* Modern Content Container */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        style={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          borderRadius: "20px",
          padding: "24px",
          height: "calc(100vh - 160px)",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(255, 255, 255, 0.2)"
        }}
      >
        <Row gutter={[24, 24]} style={{ flex: 1, display: "flex" }}>
          {/* Input Side */}
          <Col xs={24} lg={12} style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                <FileText size={18} style={{ color: "#6c757d" }} />
                <Title level={5} style={{ margin: 0, color: "#495057" }}>User Input</Title>
              </div>
              <div style={{ position: "relative" }}>
                <Input.TextArea
                  value={generation?.input}
                  rows={10}
                  readOnly
                  style={{
                    borderRadius: "12px",
                    background: "rgba(248, 249, 250, 0.8)",
                    border: "1px solid rgba(0, 0, 0, 0.05)",
                    fontSize: "14px",
                    resize: "none"
                  }}
                />
                <Tooltip title="Copy input">
                  <Button 
                    size="small" 
                    type="text" 
                    onClick={() => copyText(generation?.input)}
                    style={{ 
                      position: "absolute", 
                      top: "8px", 
                      right: "8px", 
                      color: "rgba(0,0,0,0.4)",
                      fontSize: "10px"
                    }}
                  >
                    📋
                  </Button>
                </Tooltip>
              </div>
            </div>

            {/* Metadata Cards */}
            <Row gutter={[12, 12]}>
              <Col span={12}>
                <Card size="small" style={{ borderRadius: "12px", background: "rgba(248, 249, 250, 0.8)", border: "1px solid rgba(0,0,0,0.05)" }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "18px", fontWeight: "600", color: "#495057", marginBottom: "4px" }}>
                      {generation?.tokens_count || 0}
                    </div>
                    <Text style={{ fontSize: "12px", color: "#6c757d", fontWeight: "500" }}>Tokens</Text>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" style={{ borderRadius: "12px", background: "rgba(248, 249, 250, 0.8)", border: "1px solid rgba(0,0,0,0.05)" }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "18px", fontWeight: "600", color: "#495057", marginBottom: "4px" }}>
                      {generation?.is_continue ? generation?.continue_generation_details?.length + 1 || 1 : 1}
                    </div>
                    <Text style={{ fontSize: "12px", color: "#6c757d", fontWeight: "500" }}>Generations</Text>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" style={{ borderRadius: "12px", background: "rgba(248, 249, 250, 0.8)", border: "1px solid rgba(0,0,0,0.05)" }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "16px", fontWeight: "600", color: "#495057", marginBottom: "4px" }}>
                      {generation?.category?.name || 'N/A'}
                    </div>
                    <Text style={{ fontSize: "12px", color: "#6c757d", fontWeight: "500" }}>Category</Text>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" style={{ borderRadius: "12px", background: "rgba(248, 249, 250, 0.8)", border: "1px solid rgba(0,0,0,0.05)" }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "16px", fontWeight: "600", color: "#495057", marginBottom: "4px" }}>
                      {generation?.copilot?.name || 'N/A'}
                    </div>
                    <Text style={{ fontSize: "12px", color: "#6c757d", fontWeight: "500" }}>Copilot</Text>
                  </div>
                </Card>
              </Col>
            </Row>
          </Col>

          {/* Output Side */}
          <Col xs={24} lg={12} style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <FileText size={18} style={{ color: "#6c757d" }} />
                <Title level={5} style={{ margin: 0, color: "#495057" }}>Generated Output</Title>
              </div>
              <Button
                onClick={() => copyText(generation?.generated_text)}
                size="small"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  background: "rgba(108, 117, 125, 0.1)",
                  border: "1px solid rgba(108, 117, 125, 0.2)",
                  borderRadius: "8px",
                  color: "#495057"
                }}
                icon={<Copy size={14} />}
              >
                Copy
              </Button>
            </div>
            
            <div style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column" }}>
              <Input.TextArea
                value={generation?.generated_text}
                readOnly
                style={{
                  borderRadius: "12px",
                  background: "rgba(248, 249, 250, 0.8)",
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                  fontSize: "14px",
                  lineHeight: "1.6",
                  resize: "none",
                  flex: 1,
                  minHeight: "400px"
                }}
              />
            </div>
          </Col>
        </Row>
      </motion.div>
    </div>
  );
};

export default GenerationDetails;
